'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  User,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const CompanyCrm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null); // null, 'success', 'error'
  const [focusedField, setFocusedField] = useState(null);
  const [newsletter, setNewsletter] = useState(false);
  const [subject, setSubject] = useState('');

  // Reset feedback message after 5 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => {
        setFeedback('');
        if (formStatus === 'success') {
          setFormStatus(null);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback, formStatus]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFeedback('');
    setFormStatus(null);

    const formData = {
      TITLE: subject || 'Website Lead',
      NAME: name,
      EMAIL: [{ VALUE: email, VALUE_TYPE: 'WORK' }],
      PHONE: [{ VALUE: phone, VALUE_TYPE: 'WORK' }],
      COMMENTS: message,
      SOURCE_ID: 'WEB',
      ASSIGNED_BY_ID: 10,
      // SUBSCRIBE: newsletter ? 'Yes' : 'No',
    };

    const url = new URL(
      'https://xspace.bitrix24.com/rest/790/qict0inkm3b2lxh6/crm.lead.add.json'
    );

    for (const key in formData) {
      if (Array.isArray(formData[key])) {
        formData[key].forEach((item, index) => {
          for (const subKey in item) {
            url.searchParams.append(
              `FIELDS[${key}][${index}][${subKey}]`,
              item[subKey]
            );
          }
        });
      } else {
        url.searchParams.append(`FIELDS[${key}]`, formData[key]);
      }
    }

    try {
      // Simulate a delay for demonstration purposes
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (data?.error) {
        setFeedback(`Error: ${data?.error_description}`);
        setFormStatus('error');
      } else {
        setFeedback('Your message has been submitted successfully!');
        setFormStatus('success');
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setSubject('');
        setNewsletter(false);
      }
    } catch (error) {
      setFeedback(
        'Error: There was a problem with your submission. Please try again.'
      );
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='relative'>
      {/* Animated background pattern */}
      <div className='absolute inset-0 opacity-5 dark:opacity-10'>
        <motion.div
          className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
          animate={{
            backgroundPosition: ['100% 100%', '0% 0%'],
          }}
          transition={{
            duration: 50,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: 'reverse',
          }}
        />
      </div>

      <AnimatePresence mode='wait'>
        {formStatus === 'success' ? (
          <motion.div
            key='success'
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className='text-center py-10'
          >
            <motion.div
              className='w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6'
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                delay: 0.2,
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-10 w-10 text-green-600 dark:text-green-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </motion.div>
            <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
              Message Sent!
            </h3>
            <p className='text-gray-600 dark:text-gray-400'>
              Thank you for contacting us. We'll get back to you shortly.
            </p>
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFormStatus(null)}
              className='mt-6 px-6 py-2 bg-gradient-to-r bg-primarycolor text-white rounded-full hover:shadow-lg transition-all duration-300'
            >
              Send Another Message
            </motion.button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className='space-y-6 relative z-10'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='relative'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Your Name
                </label>
                <div
                  className={`absolute left-3 top-[calc(50%+4px)] transform -translate-y-1/2 transition-all duration-300 ${
                    focusedField === 'name' || name
                      ? 'text-[#fe2a53] dark:text-[#38f5ef]'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <User className='w-5 h-5 mt-3' />
                </div>
                <input
                  type='text'
                  required
                  className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
                    focusedField === 'name'
                      ? 'border-[#fe2a53] dark:border-[#38f5ef]'
                      : 'border-gray-300 dark:border-gray-700'
                  } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#fe2a53]/50 dark:focus:ring-[#38f5ef]/50 focus:border-transparent transition-all duration-200`}
                  placeholder='John Doe'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                />
                <div
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r transform origin-left transition-transform duration-300 w-full ${
                    focusedField === 'name' ? 'scale-x-100' : 'scale-x-0'
                  }`}
                ></div>
              </div>

              <div className='relative'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Your Email
                </label>
                <div
                  className={`absolute left-3 top-[calc(50%+4px)] transform -translate-y-1/2 transition-all duration-300 ${
                    focusedField === 'email' || email
                      ? 'text-[#fe2a53] dark:text-[#38f5ef]'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <Mail className='w-5 h-5 mt-3' />
                </div>
                <input
                  type='email'
                  required
                  className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
                    focusedField === 'email'
                      ? 'border-[#fe2a53] dark:border-[#38f5ef]'
                      : 'border-gray-300 dark:border-gray-700'
                  } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#fe2a53]/50 dark:focus:ring-[#38f5ef]/50 focus:border-transparent transition-all duration-200`}
                  placeholder='john@example.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                />
                <div
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r transform origin-left transition-transform duration-300 w-full ${
                    focusedField === 'email' ? 'scale-x-100' : 'scale-x-0'
                  }`}
                ></div>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='relative'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Phone Number
                </label>
                <div
                  className={`absolute left-3 top-[calc(50%+4px)] transform -translate-y-1/2 transition-all duration-300 ${
                    focusedField === 'phone' || phone
                      ? 'text-[#fe2a53] dark:text-[#38f5ef]'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <Phone className='w-5 h-5 mt-3' />
                </div>
                <input
                  type='tel'
                  className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
                    focusedField === 'phone'
                      ? 'border-[#fe2a53] dark:border-[#38f5ef]'
                      : 'border-gray-300 dark:border-gray-700'
                  } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#fe2a53]/50 dark:focus:ring-[#38f5ef]/50 focus:border-transparent transition-all duration-200`}
                  placeholder='+971 50 123 4567'
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                />
                <div
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r transform origin-left transition-transform duration-300 w-full ${
                    focusedField === 'phone' ? 'scale-x-100' : 'scale-x-0'
                  }`}
                ></div>
              </div>

              <div className='relative'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Subject
                </label>
                <select
                  className={`w-full px-4 py-3 rounded-lg border ${
                    focusedField === 'subject'
                      ? 'border-[#fe2a53] dark:border-[#38f5ef]'
                      : 'border-gray-300 dark:border-gray-700'
                  } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#fe2a53]/50 dark:focus:ring-[#38f5ef]/50 focus:border-transparent transition-all duration-200`}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setFocusedField('subject')}
                  onBlur={() => setFocusedField(null)}
                >
                  <option value=''>Select a subject</option>
                  <option value='Property Inquiry'>Property Inquiry</option>
                  <option value='Viewing Request'>Viewing Request</option>
                  <option value='Investment Advice'>Investment Advice</option>
                  <option value='General Inquiry'>General Inquiry</option>
                </select>
                <div
                  className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r transform origin-left transition-transform duration-300 w-full ${
                    focusedField === 'subject' ? 'scale-x-100' : 'scale-x-0'
                  }`}
                ></div>
              </div>
            </div>

            <div className='relative'>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Message
              </label>
              <div
                className={`absolute left-3 top-10 transition-all duration-300 ${
                  focusedField === 'message' || message
                    ? 'text-[#fe2a53] dark:text-[#38f5ef]'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <MessageSquare className='w-5 h-5' />
              </div>
              <textarea
                rows={5}
                required
                className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
                  focusedField === 'message'
                    ? 'border-[#fe2a53] dark:border-[#38f5ef]'
                    : 'border-gray-300 dark:border-gray-700'
                } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#fe2a53]/50 dark:focus:ring-[#38f5ef]/50 focus:border-transparent transition-all duration-200`}
                placeholder="I'm interested in properties in Dubai Marina..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onFocus={() => setFocusedField('message')}
                onBlur={() => setFocusedField(null)}
              ></textarea>
              <div
                className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r transform origin-left transition-transform duration-300 w-full ${
                  focusedField === 'message' ? 'scale-x-100' : 'scale-x-0'
                }`}
              ></div>
            </div>

            <div className='flex items-center'>
              <input
                type='checkbox'
                id='newsletter'
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
                className='w-4 h-4 rounded border-gray-300 text-[#fe2a53] focus:ring-[#fe2a53] dark:focus:ring-[#38f5ef]'
              />
              <label
                htmlFor='newsletter'
                className='ml-2 text-sm text-gray-700 dark:text-gray-300'
              >
                Subscribe to our newsletter for updates on new properties
              </label>
            </div>

            <motion.button
              type='submit'
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-3 px-6 rounded-lg bg-gradient-to-r bg-primarycolor hover:bg-secondarycolor dark:bg-primarycolor dark:hover:bg-secondarycolor text-white hover:text-black font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${
                isSubmitting ? 'opacity-90 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='w-5 h-5 mr-2 animate-spin' />
                  Sending...
                </>
              ) : (
                <>
                  <Send className='w-5 h-5 mr-2' />
                  Send Message
                </>
              )}
            </motion.button>

            {feedback && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`p-4 rounded-xl ${
                  formStatus === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                } flex items-start`}
              >
                {formStatus === 'error' ? (
                  <AlertCircle className='w-5 h-5 mr-2 flex-shrink-0 mt-0.5' />
                ) : (
                  <CheckCircle className='w-5 h-5 mr-2 flex-shrink-0 mt-0.5' />
                )}
                <span>{feedback}</span>
              </motion.div>
            )}
          </form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyCrm;
